/**
 * React Router v7 route tree for unity/base admin dashboard.
 * ADR-006, ADR-020, E32 T-3900 / T-3903.
 *
 * Structure:
 *   / (root layout: providers)
 *   ├── / (login page)
 *   ├── /dashboard (protected, dashboard layout)
 *   │   ├── index
 *   │   ├── customers
 *   │   ├── sites
 *   │   ├── projects
 *   │   ├── roles
 *   │   ├── settings
 *   │   ├── audit
 *   │   └── users → redirect to customers
 *   ├── /admin (protected, admin layout)
 *   │   ├── agency (+ sub-routes)
 *   │   ├── agency-hub
 *   │   ├── alerts (+ sub-nav layout + sub-routes)
 *   │   ├── banner
 *   │   ├── bulk-ops
 *   │   ├── circuit-breakers
 *   │   ├── hub (+ sub-nav layout + sub-routes)
 *   │   ├── observability
 *   │   ├── pbrand/observability
 *   │   ├── reporting (+ :report export)
 *   │   ├── reseller
 *   │   └── tenants/impersonate
 *   └── * (404 catch-all)
 */

import { createBrowserRouter } from 'react-router-dom'
import Root from '@/routes/root'
import { ProtectedRoute } from '@/components/auth/ProtectedRoute'

// Lazy imports for code splitting
import { lazy, Suspense } from 'react'

const LoadingSpinner = () => (
  <div className="flex min-h-screen items-center justify-center">
    <div className="h-8 w-8 animate-spin rounded-full border-2 border-zinc-300 border-t-[#0EA5E9]" />
  </div>
)

function lazy_(fn: () => Promise<{ default: React.ComponentType<unknown> }>) {
  const Component = lazy(fn)
  return () => (
    <Suspense fallback={<LoadingSpinner />}>
      <Component />
    </Suspense>
  )
}

// Route components
const LoginPage = lazy_(() => import('@/routes/index'))
const DashboardLayout = lazy_(() => import('@/routes/dashboard/layout'))
const DashboardIndex = lazy_(() => import('@/routes/dashboard/index'))
const DashboardCustomers = lazy_(() => import('@/routes/dashboard/customers'))
const DashboardSites = lazy_(() => import('@/routes/dashboard/sites'))
const DashboardProjects = lazy_(() => import('@/routes/dashboard/projects'))
const DashboardRoles = lazy_(() => import('@/routes/dashboard/roles'))
const DashboardSettings = lazy_(() => import('@/routes/dashboard/settings'))
const DashboardAudit = lazy_(() => import('@/routes/dashboard/audit'))
const DashboardUsersRedirect = lazy_(() => import('@/routes/dashboard/users'))

const AdminLayout = lazy_(() => import('@/routes/admin/layout'))
const AgencyPage = lazy_(() => import('@/routes/admin/agency/index'))
const AgencyBranding = lazy_(() => import('@/routes/admin/agency/branding'))
const AgencyOnboard = lazy_(() => import('@/routes/admin/agency/onboard'))
const AgencyPricing = lazy_(() => import('@/routes/admin/agency/pricing'))
const AgencySites = lazy_(() => import('@/routes/admin/agency/sites'))
const AgencyTeam = lazy_(() => import('@/routes/admin/agency/team'))
const AgencyHub = lazy_(() => import('@/routes/admin/agency-hub'))
const AlertsLayout = lazy_(() => import('@/routes/admin/alerts/layout'))
const AlertsIndex = lazy_(() => import('@/routes/admin/alerts/index'))
const AlertsConfig = lazy_(() => import('@/routes/admin/alerts/config'))
const AlertsChannels = lazy_(() => import('@/routes/admin/alerts/channels'))
const AlertsHistory = lazy_(() => import('@/routes/admin/alerts/history'))
const BannerPage = lazy_(() => import('@/routes/admin/banner'))
const BulkOpsPage = lazy_(() => import('@/routes/admin/bulk-ops'))
const CircuitBreakersPage = lazy_(() => import('@/routes/admin/circuit-breakers'))
const HubLayout = lazy_(() => import('@/routes/admin/hub/layout'))
const HubIndex = lazy_(() => import('@/routes/admin/hub/index'))
const HubHealth = lazy_(() => import('@/routes/admin/hub/health'))
const HubSites = lazy_(() => import('@/routes/admin/hub/sites/index'))
const HubSiteDetail = lazy_(() => import('@/routes/admin/hub/sites/$siteId'))
const ObservabilityPage = lazy_(() => import('@/routes/admin/observability'))
const PbrandIndex = lazy_(() => import('@/routes/admin/pbrand/index'))
const PbrandObservability = lazy_(() => import('@/routes/admin/pbrand/observability/index'))
const ReportingPage = lazy_(() => import('@/routes/admin/reporting/index'))
const ReportExport = lazy_(() => import('@/routes/admin/reporting/$report'))
const ResellerPage = lazy_(() => import('@/routes/admin/reseller'))
const ImpersonatePage = lazy_(() => import('@/routes/admin/tenants/impersonate'))

function NotFound() {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4">
      <h1 className="text-2xl font-semibold text-zinc-900 dark:text-white">404 — Page not found</h1>
      <a href="/dashboard" className="text-[#0EA5E9] hover:underline">Go to dashboard</a>
    </div>
  )
}

export const router = createBrowserRouter([
  {
    path: '/',
    element: <Root />,
    children: [
      { index: true, element: <LoginPage /> },
      {
        element: <ProtectedRoute />,
        children: [
          {
            path: 'dashboard',
            element: <DashboardLayout />,
            children: [
              { index: true, element: <DashboardIndex /> },
              { path: 'customers', element: <DashboardCustomers /> },
              { path: 'sites', element: <DashboardSites /> },
              { path: 'projects', element: <DashboardProjects /> },
              { path: 'roles', element: <DashboardRoles /> },
              { path: 'settings', element: <DashboardSettings /> },
              { path: 'audit', element: <DashboardAudit /> },
              { path: 'users', element: <DashboardUsersRedirect /> },
            ],
          },
          {
            path: 'admin',
            element: <AdminLayout />,
            children: [
              {
                path: 'agency',
                children: [
                  { index: true, element: <AgencyPage /> },
                  { path: 'branding', element: <AgencyBranding /> },
                  { path: 'onboard', element: <AgencyOnboard /> },
                  { path: 'pricing', element: <AgencyPricing /> },
                  { path: 'sites', element: <AgencySites /> },
                  { path: 'team', element: <AgencyTeam /> },
                ],
              },
              { path: 'agency-hub', element: <AgencyHub /> },
              {
                path: 'alerts',
                element: <AlertsLayout />,
                children: [
                  { index: true, element: <AlertsIndex /> },
                  { path: 'config', element: <AlertsConfig /> },
                  { path: 'channels', element: <AlertsChannels /> },
                  { path: 'history', element: <AlertsHistory /> },
                ],
              },
              { path: 'banner', element: <BannerPage /> },
              { path: 'bulk-ops', element: <BulkOpsPage /> },
              { path: 'circuit-breakers', element: <CircuitBreakersPage /> },
              {
                path: 'hub',
                element: <HubLayout />,
                children: [
                  { index: true, element: <HubIndex /> },
                  { path: 'health', element: <HubHealth /> },
                  {
                    path: 'sites',
                    children: [
                      { index: true, element: <HubSites /> },
                      { path: ':siteId', element: <HubSiteDetail /> },
                    ],
                  },
                ],
              },
              { path: 'observability', element: <ObservabilityPage /> },
              {
                path: 'pbrand',
                children: [
                  { index: true, element: <PbrandIndex /> },
                  { path: 'observability', element: <PbrandObservability /> },
                ],
              },
              {
                path: 'reporting',
                children: [
                  { index: true, element: <ReportingPage /> },
                  { path: 'export/:report', element: <ReportExport /> },
                ],
              },
              { path: 'reseller', element: <ResellerPage /> },
              {
                path: 'tenants',
                children: [
                  { path: 'impersonate', element: <ImpersonatePage /> },
                ],
              },
            ],
          },
        ],
      },
      { path: '*', element: <NotFound /> },
    ],
  },
])
